import { useQueryClient } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useCallback, useSyncExternalStore } from "react";
import { toIsoDate } from "../utils/scheduleWeeks";
import { scheduleKeys } from "./scheduleCache";

const DAYS_PER_WEEK = 7;
const KEY_WEEK_START_INDEX = scheduleKeys.department("").length;

/**
 * When the department's cached schedule weeks holding `from`..`to` last changed (ms; 0 while none is cached), for work
 * that must follow the schedule, like asking the server again, without fetching the weeks itself.
 */
export const useScheduleDataUpdatedAt = (departmentId: string, from: DateTime | undefined, to: DateTime | undefined): number => {
	const queryCache = useQueryClient().getQueryCache();
	// A week holds `from` when it starts at most 6 days before it; the key is the week's first date.
	const firstWeekStart = from && toIsoDate(from.minus({ days: DAYS_PER_WEEK - 1 }));
	const lastWeekStart = to && toIsoDate(to);
	const subscribe = useCallback((onChange: () => void) => queryCache.subscribe(onChange), [queryCache]);
	const getSnapshot = useCallback((): number => {
		if (!firstWeekStart || !lastWeekStart) return 0;
		return queryCache.findAll({ queryKey: scheduleKeys.department(departmentId) }).reduce((latest, query) => {
			const weekStart = query.queryKey[KEY_WEEK_START_INDEX];
			const holdsRange = typeof weekStart === "string" && weekStart >= firstWeekStart && weekStart <= lastWeekStart;
			return holdsRange ? Math.max(latest, query.state.dataUpdatedAt) : latest;
		}, 0);
	}, [queryCache, departmentId, firstWeekStart, lastWeekStart]);
	return useSyncExternalStore(subscribe, getSnapshot);
};
